import { Logger, LogSender } from '../../../logger';
import { StreamingEventBus } from '../../abstract/StreamingEventBus';
import StreamingError, {
  StreamingErrorCodes,
} from '../../abstract/StreamingError';
import { StreamingSubscriptionEventHandler } from '../../abstract/StreamingSubscription';
import {
  DoryMessageType,
  DoryServiceType,
  DoryStreamingMessage,
} from '../../abstract/StreamingMessages';
import {
  QuoteRequestMessageResponse,
  QuoteRequestCancellationResponse,
  QuoteRequestPayload,
  QuoteRequestRequest,
  getQuoteHeader,
  QuoteRequestRejectResponse,
  QuoteExecutionRequest,
  QuoteExecutionReportResponse,
  QuoteRejectRequest,
  QuoteRejectResponse,
} from './Messages';

//#region QuoteRequest
export type QuoteRequestOnMessage = (
  msg: QuoteRequestMessageResponse,
  err: StreamingError
) => void;
export type QuoteRequestOnReject = (
  msg: QuoteRequestRejectResponse,
  err: StreamingError
) => void;
export type QuoteRequestOnCancellation = (
  msg: QuoteRequestCancellationResponse,
  err: StreamingError
) => void;
//#endregion

//#region QuoteReject
export type QuoteRejectOnConfirm = (
  msg: QuoteRejectResponse,
  err: StreamingError
) => void;
//#endregion

// #region QuoteTrade
export type QuoteExecutionOnReport = (
  msg: QuoteExecutionReportResponse,
  err: StreamingError
) => void;
//#endregion

export enum TradeState {
  PENDING = 0,
  OFFER = 1,
  ACCEPTED = 2,
  CANCELLED = 3,
  REJECTED = 4,
}

export default class QuoteTradeSubscription {
  private id: string = undefined;
  private eventBus: StreamingEventBus = undefined;
  private quoteId: string = undefined;
  private data: QuoteRequestPayload;

  private _state: TradeState;

  public get state(): TradeState {
    return this._state;
  }

  public get SubscribeRequest(): QuoteRequestRequest {
    return {
      header: {
        ...getQuoteHeader(DoryMessageType.QUOTEREQUEST),
        channelID: this.id,
      },
      payload: this.data,
    };
  }

  public get RejectRequest(): QuoteRejectRequest {
    return {
      header: {
        ...getQuoteHeader(DoryMessageType.QUOTEREJECT),
        channelID: this.id,
      },
      payload: {
        quoteID: this.quoteId,
        quoteRespType: 'END_TRADE',
      },
    };
  }

  public get ExecutionRequest(): QuoteExecutionRequest {
    return {
      header: {
        ...getQuoteHeader(DoryMessageType.QUOTEEXECUTION),
        channelID: this.id,
      },
      payload: {
        quoteId: this.quoteId,
      },
    };
  }

  public readonly on: {
    [key: string]: StreamingSubscriptionEventHandler<any>;
    // Quote Request
    [DoryMessageType.QUOTEREQUESTMESSAGE]: StreamingSubscriptionEventHandler<QuoteRequestMessageResponse>;
    [DoryMessageType.QUOTEREQUESTREJECT]: StreamingSubscriptionEventHandler<QuoteRequestRejectResponse>;
    [DoryMessageType.QUOTEREQUESTCANCELLATION]: StreamingSubscriptionEventHandler<QuoteRequestCancellationResponse>;
    // Quote Reject
    [DoryMessageType.QUOTEREJECTCONFIRMEND]: StreamingSubscriptionEventHandler<QuoteRejectResponse>;
    // Quote Execution
    [DoryMessageType.QUOTEEXECUTIONREPORT]: StreamingSubscriptionEventHandler<QuoteExecutionReportResponse>;
  } = {
    // Quote Request
    [DoryMessageType.QUOTEREQUESTMESSAGE]: {
      handler: undefined,
      isAttached: false,
      attach: () => {
        const messageListener = this.eventBus.on<QuoteRequestMessageResponse>(
          DoryServiceType.FIXTRADESRET,
          DoryMessageType.QUOTEREQUESTMESSAGE,
          (data) => {
            if (data.header.channelID === this.id) {
              this.on[DoryMessageType.QUOTEREQUESTMESSAGE].handler(
                data,
                undefined
              );
              // store quoteID for later use
              this.quoteId = data.payload.quoteID;
              // update trading state
              this._state = TradeState.OFFER;
              // stop timeout
              this.stopTimeout();
              // detach listener
              this.on[DoryMessageType.QUOTEREQUESTMESSAGE]?.detach();
              this.on[DoryMessageType.QUOTEREQUESTMESSAGE].detach = undefined;
              this.on[DoryMessageType.QUOTEREQUESTMESSAGE].isAttached = false;
              Logger.silly(
                LogSender.STREAMING_QUOTETRADE,
                'QuoteRequest Message handler stopped'
              );
            }
          }
        );
        this.on[DoryMessageType.QUOTEREQUESTMESSAGE].isAttached = true;
        this.on[DoryMessageType.QUOTEREQUESTMESSAGE].detach = messageListener;
      },
    },
    [DoryMessageType.QUOTEREQUESTREJECT]: {
      handler: undefined,
      isAttached: false,
      attach: () => {
        const handler = (data: QuoteRequestRejectResponse) => {
          if (data.header.channelID === this.id) {
            this.on[DoryMessageType.QUOTEREQUESTREJECT].handler(
              data,
              undefined
            );
            // update trading state
            this._state = TradeState.REJECTED;
            // stop timeout
            this.stopTimeout();
            // detach all listeners
            this.stop();
            Logger.silly(
              LogSender.STREAMING_QUOTETRADE,
              `Received Request Reject, stopped all listeners for channel ${this.id}`
            );
          }
        };
        // Server can send one of two reject messages
        const requestFailedListener =
          this.eventBus.on<QuoteRequestRejectResponse>(
            DoryServiceType.FIXTRADESRET,
            DoryMessageType.QUOTEREQUESTFAILED,
            handler
          );
        const flowTerminationListener =
          this.eventBus.on<QuoteRequestRejectResponse>(
            DoryServiceType.FIXTRADESRET,
            DoryMessageType.QUOTEREQUESTFLOWTERMINATION,
            handler
          );
        this.on[DoryMessageType.QUOTEREQUESTMESSAGE].isAttached = true;
        this.on[DoryMessageType.QUOTEREQUESTMESSAGE].detach = () => {
          requestFailedListener();
          flowTerminationListener();
        };
      },
    },
    [DoryMessageType.QUOTEREQUESTCANCELLATION]: {
      handler: undefined,
      isAttached: false,
      attach: () => {
        const cancellationListener =
          this.eventBus.on<QuoteRequestCancellationResponse>(
            DoryServiceType.FIXTRADESRET,
            DoryMessageType.QUOTEREQUESTCANCELLATION,
            (data) => {
              // Check if either:
              // - header.channelID matches
              // - payload.quoteCancelType matches "Cancel All Quotes"
              if (
                data.header.channelID === this.id ||
                data.payload.quoteCancelType === 'Cancel All Quotes'
              ) {
                this.on[DoryMessageType.QUOTEREQUESTCANCELLATION].handler(
                  data,
                  undefined
                );
                // update trading state
                this._state = TradeState.CANCELLED;
                // detach all listeners
                this.stop();
                Logger.silly(
                  LogSender.STREAMING_QUOTETRADE,
                  `Received Cancellation, stopped all listeners for channel ${this.id}`
                );
              }
            }
          );
        this.on[DoryMessageType.QUOTEREQUESTCANCELLATION].isAttached = true;
        this.on[DoryMessageType.QUOTEREQUESTCANCELLATION].detach =
          cancellationListener;
      },
    },
    // Quote Reject
    [DoryMessageType.QUOTEREJECTCONFIRMEND]: {
      handler: undefined,
      isAttached: false,
      attach: () => {
        const messageListener = this.eventBus.on<QuoteRejectResponse>(
          DoryServiceType.FIXTRADESRET,
          DoryMessageType.QUOTEREJECTCONFIRMEND,
          (data) => {
            if (data.header.channelID === this.id) {
              this.on[DoryMessageType.QUOTEREJECTCONFIRMEND].handler(
                data,
                undefined
              );
              // update trading state
              this._state = TradeState.ACCEPTED;
              // stop timeout
              this.stopTimeout();
              // detach all listeners
              this.stop();
              Logger.silly(
                LogSender.STREAMING_QUOTETRADE,
                `Received Reject Confirmation, stopped all listeners for channel ${this.id}`
              );
            }
          }
        );
        this.on[DoryMessageType.QUOTEREJECTCONFIRMEND].isAttached = true;
        this.on[DoryMessageType.QUOTEREJECTCONFIRMEND].detach = messageListener;
      },
    },
    // Quote Execution
    [DoryMessageType.QUOTEEXECUTIONREPORT]: {
      handler: undefined,
      isAttached: false,
      attach: () => {
        let receivedCount = 0;
        const messageListener = this.eventBus.on<QuoteExecutionReportResponse>(
          DoryServiceType.FIXTRADESRET,
          DoryMessageType.QUOTEEXECUTIONREPORT,
          (data) => {
            if (data.header.channelID === this.id) {
              this.on[DoryMessageType.QUOTEEXECUTIONREPORT].handler(
                data,
                undefined
              );
              receivedCount += 1;

              if (receivedCount === 2) {
                // update trading state
                this._state = TradeState.ACCEPTED;
                // stop timeout
                this.stopTimeout();
                // detach all listeners
                this.stop();
                Logger.silly(
                  LogSender.STREAMING_QUOTETRADE,
                  `Received Execution Report, stopped all listeners for channel ${this.id}`
                );
              }
            }
          }
        );
        this.on[DoryMessageType.QUOTEEXECUTIONREPORT].isAttached = true;
        this.on[DoryMessageType.QUOTEEXECUTIONREPORT].detach = messageListener;
      },
    },
  };

  constructor(
    id: string,
    eventBus: StreamingEventBus,
    data: QuoteRequestPayload
  ) {
    this.id = id;
    this.eventBus = eventBus;
    this.data = data;

    this._state = TradeState.PENDING;
  }

  public request(
    timeout: number,
    onMessage: QuoteRequestOnMessage,
    onReject: QuoteRequestOnReject,
    onCancellation: QuoteRequestOnCancellation
  ): void {
    const source = 'Streaming.QuoteTrade.QuoteTradeSubscription.request';

    this.on[DoryMessageType.QUOTEREQUESTMESSAGE].handler = onMessage;
    this.on[DoryMessageType.QUOTEREQUESTREJECT].handler = onReject;
    this.on[DoryMessageType.QUOTEREQUESTCANCELLATION].handler = onCancellation;

    this.on[DoryMessageType.QUOTEREQUESTMESSAGE].attach();
    this.on[DoryMessageType.QUOTEREQUESTREJECT].attach();
    this.on[DoryMessageType.QUOTEREQUESTCANCELLATION].attach();

    this.startTimeout(
      timeout,
      source,
      this.on[DoryMessageType.QUOTEREQUESTREJECT].handler
    );
  }

  public reject(timeout: number, onReject: QuoteRejectOnConfirm): void {
    const source = 'Streaming.QuoteTrade.QuoteTradeSubscription.reject';

    this.on[DoryMessageType.QUOTEREJECTCONFIRMEND].handler = onReject;

    this.on[DoryMessageType.QUOTEREJECTCONFIRMEND].attach();

    this.startTimeout(
      timeout,
      source,
      this.on[DoryMessageType.QUOTEREJECTCONFIRMEND].handler
    );
  }

  public confirm(timeout: number, onReport: QuoteExecutionOnReport): void {
    const source = 'Streaming.QuoteTrade.QuoteTradeSubscription.confirm';

    this.on[DoryMessageType.QUOTEEXECUTIONREPORT].handler = onReport;

    this.on[DoryMessageType.QUOTEEXECUTIONREPORT].attach();

    this.startTimeout(
      timeout,
      source,
      this.on[DoryMessageType.QUOTEEXECUTIONREPORT].handler
    );
  }

  private stop() {
    for (const messageType in this.on) {
      if (this.on[messageType].detach) {
        this.on[messageType]?.detach();
        this.on[messageType].detach = undefined;
        this.on[messageType].isAttached = false;
        Logger.silly(
          LogSender.STREAMING_QUOTETRADE,
          `Stopping ${messageType} listener`
        );
      }
    }

    this.stopTimeout();
  }

  //#region Timeout
  private pendingTimeout: number;
  protected startTimeout(
    timeout: number,
    source: string,
    handler: (msg: DoryStreamingMessage, err: StreamingError) => void
  ): void {
    this.pendingTimeout = window.setTimeout(() => {
      handler(
        undefined,
        new StreamingError(
          source,
          StreamingErrorCodes.TIMEOUT,
          'Request to WebSocket timed out!'
        )
      );
      this.stopTimeout();
    }, timeout);
  }
  protected stopTimeout(): void {
    if (this.pendingTimeout) {
      window.clearTimeout(this.pendingTimeout);
      this.pendingTimeout = undefined;
    }
  }
  //#endregion
}
